import { buildApiUrl } from '../utils/apiSetup';
const authHeaders = (json = true) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
};

const parseError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      return data.detail[0].msg || fallback;
    }
  } catch {
    /* ignore */
  }
  return fallback;
};

// ============================================================
// EMPLOYEE DASHBOARD APIs ("My PF")
// ============================================================

export async function fetchMyPFAccount(signal) {
  const response = await fetch(buildApiUrl('/api/v1/pf/my-account'), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load PF account summary'));
  return response.json();
}

export async function fetchMyPFLedger(page = 1, pageSize = 20, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/my-ledger?page=${page}&page_size=${pageSize}`), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load PF ledger'));
  return response.json();
}

export async function fetchMyPFAdvances(signal) {
  const response = await fetch(buildApiUrl('/api/v1/pf/my-advances'), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load PF advance requests'));
  return response.json();
}

export async function fetchMyPFRecovery(signal) {
  const response = await fetch(buildApiUrl('/api/v1/pf/my-recovery'), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load active recovery details'));
  return response.json();
}

export async function submitPFAdvanceRequest(payload, signal) {
  const response = await fetch(buildApiUrl('/api/v1/pf/advance-request'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to submit PF advance request'));
  return response.json();
}

export async function submitEmployeePFAdvanceRequest(employeeId, payload, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/employee/${employeeId}/advance-request`), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to submit employee PF advance request'));
  return response.json();
}

// ============================================================
// EMPLOYEE360 & ADMIN APIs
// ============================================================

export async function fetchEmployeePFAccount(employeeId, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/employee/${employeeId}/account`), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load employee PF account'));
  return response.json();
}

export async function fetchEmployeePFLedger(employeeId, page = 1, pageSize = 20, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/employee/${employeeId}/ledger?page=${page}&page_size=${pageSize}`), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load employee PF ledger'));
  return response.json();
}

export async function fetchEmployeePFAdvances(employeeId, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/employee/${employeeId}/advances`), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load employee PF advances'));
  return response.json();
}

export async function fetchEmployeePFRecovery(employeeId, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/employee/${employeeId}/recovery`), {
    headers: authHeaders(false),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to load employee active recovery'));
  return response.json();
}

export async function approvePFAdvanceRequest(requestId, formData, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/advance/${requestId}/approve`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    body: formData,
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to approve PF advance request'));
  return response.json();
}

export async function rejectPFAdvanceRequest(requestId, payload, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/advance/${requestId}/reject`), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to reject PF advance request'));
  return response.json();
}

export async function confirmPFAdvancePayment(requestId, formData, signal) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/advance/${requestId}/confirm-payment`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    body: formData,
    signal,
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to confirm PF advance payment'));
  return response.json();
}

export async function downloadAdvanceDocument(requestId) {
  const response = await fetch(buildApiUrl(`/api/v1/pf/advance/${requestId}/document`), {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to download document'));
  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const rawBlob = await response.blob();
  const blob = rawBlob.type ? rawBlob : new Blob([rawBlob], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const newWin = window.open(url, '_blank');
  if (!newWin) {
    // If popup blocker blocked it, fallback to download
    const a = document.createElement('a');
    a.href = url;
    a.download = `pf_advance_${requestId}_document`;
    a.click();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}
